import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { debounce } from 'lodash';
import {
  isAfter,
  isBefore,
  parseISO,
  isToday,
  isYesterday,
  isAfter as dateIsAfter,
  isThisYear,
  subDays,
  startOfMonth,
  endOfMonth,
  isValid
} from 'date-fns';
import { format } from 'date-fns';
import { getChats } from '../../services/AGTChatService';

export function useChatFilters(user, chats, selectedCategory, setChats, setHasNextPage, setLastEvaluatedKey, setError, setRetryAction) {
  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [lastEvaluatedKey, setLocalLastEvaluatedKey] = useState(null);
  
  // Referencia para la función de búsqueda debounced
  const searchRef = useRef(null);

  // Determinar categoría basada en fecha
  const getChatCategory = useCallback((chat) => {
    if (!chat.updatedAt && !chat.createdAt) {
      console.warn('Chat sin fecha:', chat);
      return 'Unknown';
    }
    const date = parseISO(chat.updatedAt || chat.createdAt);
    if (!isValid(date)) {
      console.warn('Fecha inválida para el chat:', chat);
      return 'Unknown';
    }
    const now = new Date();
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    if (dateIsAfter(date, subDays(now, 7))) return 'Previous 7 Days';
    if (dateIsAfter(date, subDays(now, 30))) return 'Previous 30 Days';
    if (isThisYear(date)) {
      return format(date, 'MMMM');
    }
    return format(date, 'yyyy');
  }, []);

  // Función de búsqueda real
  const performSearch = useCallback(async (term) => {
    if (!user?.uid) return;

    try {
      setIsNextPageLoading(true);

      if (!term.trim()) {
        // Si no hay término de búsqueda, volvemos al modo normal
        setIsSearchMode(false);
        const options = {
          limit: 20,
          category: selectedCategory !== 'add' ? selectedCategory : null
        };
        const response = await getChats(user.uid, options);
        setChats(response.chats);
        setHasNextPage(response.hasMore);
        setLastEvaluatedKey?.(response.lastEvaluatedKey);
        setLocalLastEvaluatedKey(response.lastEvaluatedKey);
        return;
      }

      setIsSearchMode(true);

      const response = await getChats(user.uid, {
        limit: 50,
        searchTerm: term,
        category: selectedCategory !== 'add' ? selectedCategory : null
      });

      setChats(response.chats);
      setHasNextPage(response.hasMore);
      setLastEvaluatedKey?.(response.lastEvaluatedKey);
      setLocalLastEvaluatedKey(response.lastEvaluatedKey);
      setTotalSearchResults(response.total || 0);

    } catch (error) {
      console.error('Error searching chats:', error);
      setError?.('Error searching chats. Please try again.');
      setRetryAction?.(() => () => searchRef.current(term));
    } finally {
      setIsNextPageLoading(false);
    }
  }, [user?.uid, selectedCategory, setChats, setHasNextPage, setLastEvaluatedKey, setError, setRetryAction, setIsNextPageLoading, setIsSearchMode, setLocalLastEvaluatedKey, setTotalSearchResults]);

  // Crear y mantener la función debounced actualizada
  useEffect(() => {
    searchRef.current = debounce(performSearch, 300);
    
    return () => {
      // Cancelar el debounce al desmontar
      searchRef.current?.cancel();
    };
  }, [performSearch]);

  // Función para llamar a la búsqueda debounced
  const debouncedSearch = useCallback((term) => {
    if (searchRef.current) {
      searchRef.current(term);
    }
  }, []);

  // Controladores de eventos
  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    if (!searchTerm.trim()) {
      setIsSearchFocused(false);
    }
  }, [searchTerm]);

  const handleSearchChange = useCallback((e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (term.trim()) {
      setIsSearchFocused(true);
    } else {
      setIsSearchFocused(false);
    }

    // Invocamos la búsqueda debounced
    debouncedSearch(term);
  }, [debouncedSearch]);

  const toggleFilter = useCallback(() => {
    setIsFilterOpen((prev) => !prev);
  }, []);

  const handleFilterToggle = useCallback((filter) => {
    setSelectedFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
    );
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedFilters([]);
    setSearchTerm('');
    setDateFilter(null);
    setIsSearchFocused(false);
    debouncedSearch('');
  }, [debouncedSearch]);

  // Funciones de filtrado
  const filterByCategory = useCallback((inputChats, category) => {
    if (!category || category === 'add') return inputChats;
    return inputChats.filter(chat => chat.category === category);
  }, []);
   
  const filterBySearch = useCallback((inputChats, term) => {
    if (!term.trim()) return inputChats;
    const lowerTerm = term.toLowerCase();
    return inputChats.filter(chat => 
      chat.title?.toLowerCase().includes(lowerTerm)
    );
  }, []);
   
  const filterByDateFilters = useCallback((inputChats, filters) => {
    if (!filters.length) return inputChats;
    return inputChats.filter(chat => {
      const category = getChatCategory(chat);
      return (
        filters.includes(category) ||
        (filters.includes('Older') &&
          !['Today', 'Yesterday', 'Previous 7 Days', 'Previous 30 Days'].includes(category))
      );
    });
  }, [getChatCategory]);
   
  const filterByDateRange = useCallback((inputChats, date) => {
    if (!date) return inputChats;
    return inputChats.filter(chat => {
      const chatDate = parseISO(chat.updatedAt || chat.createdAt);
      return (
        isValid(chatDate) &&
        isAfter(chatDate, startOfMonth(date)) &&
        isBefore(chatDate, endOfMonth(date))
      );
    });
  }, []);
   
  const sortChats = useCallback((inputChats) => {
    return [...inputChats].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt);
      const dateB = new Date(b.updatedAt || b.createdAt);
      return dateB - dateA;
    });
  }, []);

  // Aplicación de filtros completa
  const filteredChats = useMemo(() => {
    // Apply filters in sequence, only when needed
    let result = chats;
   
    // Early return for empty chats
    if (!result.length) return result;
   
    // Apply category filter
    if (selectedCategory && selectedCategory !== 'add') {
      result = filterByCategory(result, selectedCategory);
    }
   
    // Apply search filter
    if (searchTerm.trim()) {
      result = filterBySearch(result, searchTerm);
    }
   
    // Apply date filters
    if (selectedFilters.length > 0) {
      result = filterByDateFilters(result, selectedFilters);
    }
   
    // Apply date range
    if (dateFilter) {
      result = filterByDateRange(result, dateFilter);
    }
   
    // Sort results
    return sortChats(result);
  }, [
    chats,
    selectedCategory,
    searchTerm,
    selectedFilters,
    dateFilter,
    filterByCategory,
    filterBySearch,
    filterByDateFilters,
    filterByDateRange,
    sortChats
  ]);

  // Definición de categorías de filtro disponibles
  const filterCategories = useMemo(
    () => ({
      main: [
        { name: 'Today', tooltip: 'Show chats from today' },
        { name: 'Yesterday', tooltip: 'Show chats from yesterday' },
      ],
      extra: [
        { name: 'Previous 7 Days', tooltip: 'Show chats from the last week' },
        { name: 'Previous 30 Days', tooltip: 'Show chats from the last month' },
        { name: 'Older', tooltip: 'Show older chats' },
      ],
    }),
    []
  );

  // Función para cargar más resultados
  const loadMoreFilteredChats = useCallback(async (loadMore = false) => {
    if (!user || !user.uid || (loadMore && isNextPageLoading)) return;

    setIsNextPageLoading(true);

    try {
      // Opciones base
      const options = {
        limit: isSearchMode ? 50 : 20,
        lastEvaluatedKey: loadMore ? lastEvaluatedKey : null,
        category: selectedCategory !== 'add' ? selectedCategory : null
      };

      // Añadir término de búsqueda si está en modo búsqueda
      if (isSearchMode && searchTerm) {
        options.searchTerm = searchTerm;
      }

      const response = await getChats(user.uid, options);

      if (!loadMore) {
        setChats(response.chats);
      } else {
        setChats(prevChats => {
          const uniqueChats = [...prevChats];

          response.chats.forEach(chat => {
            if (!uniqueChats.some(existing => existing.id === chat.id)) {
              uniqueChats.push(chat);
            }
          });

          return uniqueChats;
        });
      }

      setHasNextPage(response.hasMore);
      setLastEvaluatedKey?.(response.lastEvaluatedKey);
      setLocalLastEvaluatedKey(response.lastEvaluatedKey);

      if (isSearchMode) {
        setTotalSearchResults(response.total || 0);
      }

    } catch (error) {
      console.error('Error loading filtered chats:', error);
      setError?.('Error loading chats. Please try again.');
      setRetryAction?.(() => () => loadMoreFilteredChats(loadMore));
    } finally {
      setIsNextPageLoading(false);
    }
  }, [user, lastEvaluatedKey, isNextPageLoading, isSearchMode, searchTerm, selectedCategory, setChats, setHasNextPage, setLastEvaluatedKey, setError, setRetryAction]);

  // Cargar chats automáticamente al montar y al cambiar de categoría
  useEffect(() => {
    if (user?.uid && !searchTerm.trim()) {
      // Solo cargar si no hay búsqueda activa
      loadMoreFilteredChats(false);
    }
  }, [user?.uid, selectedCategory]); // Se ejecuta al montar y cuando cambia la categoría

  return {
    // Estados
    searchTerm,
    setSearchTerm,
    dateFilter,
    setDateFilter,
    selectedFilters,
    setSelectedFilters,
    isFilterOpen,
    setIsFilterOpen,
    showMoreFilters,
    setShowMoreFilters,
    isSearchMode,
    setIsSearchMode,
    totalSearchResults,
    isSearchFocused,
    setIsSearchFocused,
    isNextPageLoading,
    
    // Handlers
    handleSearchFocus,
    handleSearchBlur,
    handleSearchChange,
    toggleFilter,
    handleFilterToggle,
    clearAllFilters,
    
    // Filtros y categorización
    getChatCategory,
    filterCategories,
    filteredChats,
    
    // Carga de más resultados
    loadMoreFilteredChats,
    
    // Categorización de chats
    filterByCategory,
    filterBySearch,
    filterByDateFilters,
    filterByDateRange,
    sortChats
  };
}